import { File } from '@ms/yammer-data/dist/state/types';
import FeedStrings from '@ms/yammer-strings/dist/Feed';

export type FileType = 'code' | 'pptx' | 'pdf' | 'photo' | 'audio' | 'xlsx' | 'docx' | 'txt' | 'video' | 'zip' | 'one';

type FileTypeToMimeTypeMap = { readonly key: FileType; readonly values: string[] }[];
const fileTypeToMimeTypeMap: FileTypeToMimeTypeMap = [
  {
    key: 'code',
    values: [
      'text/x-csrc',
      'text/x-c++src',
      'text/css',
      'text/x-ruby',
      'text/x-php',
      'text/x-java-source',
      'application/javascript',
      'text/x-perl',
      'application/xml',
      'text/html',
    ],
  },
  {
    key: 'pptx',
    values: [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-powerpoint.presentation.macroenabled.12',
      'application/vnd.ms-powerpoint.addin.macroenabled.12',
      'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
      'application/vnd.ms-powerpoint.slideshow.macroenabled.12',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.template',
      'application/vnd.ms-powerpoint.template.macroenabled.12',
    ],
  },
  {
    key: 'pdf',
    values: ['application/pdf'],
  },
  {
    key: 'photo',
    values: ['image/png', 'image/jpeg', 'image/gif'],
  },
  {
    key: 'audio',
    values: ['audio/mpeg', 'audio/mobile-xmf', 'audio/x-flac', 'audio/vorbis'],
  },
  {
    key: 'xlsx',
    values: [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel.sheet.macroenabled.12',
      'application/vnd.ms-excel.sheet.binary.macroenabled.12',
      'application/vnd.ms-excel.addin.macroenabled.12',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
      'application/vnd.ms-excel.template.macroenabled.12',
      'text/csv',
      'text/tab-separated-values',
      'application/vnd.oasis.opendocument.spreadsheet',
    ],
  },
  {
    key: 'docx',
    values: [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-word.document.macroenabled.12',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
      'application/vnd.ms-word.template.macroenabled.12',
    ],
  },
  {
    key: 'txt',
    values: ['text/plain', 'application/rtf'],
  },
  {
    key: 'video',
    values: [
      'video/x-ms-asf',
      'video/x-msvideo',
      'audio/x-mod',
      'video/x-m4v',
      'video/mpeg',
      'video/x-ms-wmv',
      'video/3gpp',
      'video/quicktime',
      'video/mp4',
      'video/mpeg',
      'video/webm',
      'video/x-matroska',
    ],
  },
  {
    key: 'zip',
    values: ['application/zip', 'application/x-tar', 'application/gzip'],
  },
  {
    key: 'one',
    values: ['application/onenote'],
  },
];

type MimeTypeToFileTypeMap = Record<string, FileType>;
const invertKeyValuePairMappings: (mappings: FileTypeToMimeTypeMap) => MimeTypeToFileTypeMap = (mappings) => {
  const invertedMapping: MimeTypeToFileTypeMap = {};
  mappings.forEach((mapping) => {
    const values = mapping.values;
    const key = mapping.key;
    values.forEach((value) => {
      invertedMapping[value] = key;
    });
  });

  return invertedMapping;
};

const mimeTypeToFileTypeMap: MimeTypeToFileTypeMap = invertKeyValuePairMappings(fileTypeToMimeTypeMap);

export const getFileTypeFromMimeType: (mimeType: string) => FileType = (mimeType) => mimeTypeToFileTypeMap[mimeType];

const methods = {
  code: (t: FeedStrings) => t.filePreviewAttachedCodeFile(),
  pptx: (t: FeedStrings) => t.filePreviewAttachedPresentationFile(),
  pdf: (t: FeedStrings) => t.filePreviewAttachedPDFFile(),
  photo: (t: FeedStrings) => t.filePreviewAttachedImageFile(),
  audio: (t: FeedStrings) => t.filePreviewAttachedAudioFile(),
  xlsx: (t: FeedStrings) => t.filePreviewAttachedSpreadsheetFile(),
  docx: (t: FeedStrings) => t.filePreviewAttachedWordFile(),
  txt: (t: FeedStrings) => t.filePreviewAttachedTextFile(),
  video: (t: FeedStrings) => t.filePreviewAttachedVideoFile(),
  zip: (t: FeedStrings) => t.filePreviewAttachedArchiveFile(),
  one: (t: FeedStrings) => t.filePreviewAttachedOneNoteFile(),
};

export function getFileAttachmentDescriptionFromMimeType(mimeType: File['mimeType'], t: FeedStrings) {
  const fileType = mimeTypeToFileTypeMap[mimeType];
  const method = methods[fileType];
  if (method) {
    return method(t);
  }

  return t.filePreviewAttachedFile();
}
